import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, ReceiptIcon, ActivityIcon, LandmarkIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { getPayoutById, getAccountDetails, getPayoutCalcSections, type Payout, type PayoutStatus } from '../mocks/payoutsData';
// ── Formatting helpers ────────────────────────────────────────────────────
// Dates arrive already formatted in the tenant timezone (MYT = UTC+8) with
// the timezone label attached, via `formatPayoutDate` in payoutsData — one
// formatter for every surface, so the label can't drift from the offset.
const fmtAmount = (n: number) => formatCurrencyAmount(Math.abs(n), {
  fractionDigits: 2
});
const fmtSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtAmount(n)}`;
// ── Status pill ──────────────────────────────────────────────────────────
// These labels are the SAME vocabulary as the payouts list and the status
// tooltip — Pending / Submitted / Partially submitted / Failed / Cancelled.
// This page must never invent its own status names.
const STATUS_LABEL: Record<PayoutStatus, string> = {
  submitted: 'Submitted',
  pending: 'Pending',
  partially_submitted: 'Partially submitted',
  failed: 'Failed',
  cancelled: 'Cancelled'
};
const STATUS_PILL: Record<PayoutStatus, string> = {
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-gray-50 text-gray-700 border-gray-200',
  partially_submitted: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-600 border-gray-200'
};
function StatusPill({
  status
}: {
  status: PayoutStatus;
}) {
  return <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full ${STATUS_PILL[status]}`} data-id="element-3074" data-mp="6b3tgco23pw4">
      {STATUS_LABEL[status]}
    </span>;
}
// ── Section primitive ────────────────────────────────────────────────────
function Section({
  icon: Icon,
  title,
  description,
  children,
  action
}: {
  icon?: ComponentType<{
    size?: number;
    className?: string;
  }>;
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-lg" data-id="element-3075" data-mp="13031pron4rob">
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200" data-id="element-3076" data-mp="yzsllm74wkvq">
        <div className="min-w-0" data-id="element-3077" data-mp="34shjmqr6pw4q">
          <div className="flex items-center gap-2" data-id="element-3078" data-mp="34titwt0dmele">
            {Icon && <Icon size={15} className="text-gray-500" aria-hidden="true" data-id="element-3079" data-mp="30re1fxlnzo0c" />}
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-3080" data-mp="2qn7fh2wcoy7i">{title}</h2>
          </div>
          {description && <p className={`text-xs text-gray-500 mt-0.5 ${Icon ? 'ml-[22px]' : ''}`} data-id="element-3081" data-mp="2uosp8fdl8wdh">
              {description}
            </p>}
        </div>
        {action}
      </div>
      {children}
    </section>;
}
// ── Copy button ──────────────────────────────────────────────────────────
function CopyButton({
  value,
  label,
  size = 14
}: {
  value: string;
  label: string;
  size?: number;
}) {
  const [copied, setCopied] = useState(false);
  return <button type="button" onClick={async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }} className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors" aria-label={`Copy ${label}`} data-id="element-3082" data-mp="1xytdkc255hqo">
      {copied ? <CheckIcon size={size} className="text-emerald-600" aria-hidden="true" data-id="element-3083" data-mp="1x8mm9wg0a5kc" /> : <CopyIcon size={size} aria-hidden="true" data-id="element-3084" data-mp="1s88pjajuui1m" />}
    </button>;
}
// ── Account details (expandable) ─────────────────────────────────────────
function AccountDetails({
  payout
}: {
  payout: Payout;
}) {
  const [open, setOpen] = useState(false);
  const a = getAccountDetails(payout);
  return <Section icon={LandmarkIcon} title="Destination account" data-id="element-3085" data-mp="19jk66t10skry">
      <div data-id="element-3086" data-mp="19hzct6qfz7vo">
        <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-gray-50 transition-colors" aria-expanded={open} data-id="element-3087" data-mp="19i8zolg6wyx5">
          <span className="flex items-center gap-3 min-w-0" data-id="element-3088" data-mp="vkapztctdqnz">
            {open ? <ChevronDownIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3089" data-mp="qkpopca69683" /> : <ChevronRightIcon size={16} className="text-gray-400 flex-shrink-0" aria-hidden="true" data-id="element-3090" data-mp="32ae26t4ilgo5" />}
            <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-gray-100 flex-shrink-0" data-id="element-3091" data-mp="3b99bwf6rp8w7">
              <LandmarkIcon size={16} className="text-gray-600" aria-hidden="true" data-id="element-3092" data-mp="3g5q6ns8h2li9" />
            </span>
            <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-3093" data-mp="2njyftb0d42oe">
              {a.bank}
            </span>
            <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-3094" data-mp="2sgorzn91qe4j">
              •••• {a.last4}
            </span>
          </span>
          <span className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap" data-id="element-3095" data-mp="1ztkjv9enpjoj">
            <span aria-hidden="true" data-id="element-3096" data-mp="24snqgyaabxym">{a.origin.flag}</span>
            {payout.currency}
          </span>
        </button>
        {open && <div className="px-6 pb-5 pt-1 border-t border-gray-100" data-id="element-3097" data-mp="382a0p5e1f66y">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 mt-4" data-id="element-3098" data-mp="33502p1cl1m7i">
              <KV label="Account holder" data-id="element-3099" data-mp="3gvvdij6n6ddq">{a.accountHolder}</KV>
              <KV label="Routing number" mono data-id="element-3100" data-mp="3gvd6qsmcsgrk">
                {a.routingNumber}
              </KV>
            </dl>
          </div>}
      </div>
    </Section>;
}
function KV({
  label,
  children,
  mono,
  copyable
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-start gap-3 min-w-0" data-id="element-3101" data-mp="2k7yw5c3dre1g">
      <dt className="text-xs text-gray-500 pt-0.5" data-id="element-3102" data-mp="2y3ao5bq5iqeo">{label}</dt>
      <dd className={`flex items-center gap-1 text-sm text-gray-900 min-w-0 ${mono ? 'font-mono' : ''}`} data-id="element-3103" data-mp="2t5952qsq6r37">
        <span className="truncate min-w-0" data-id="element-3104" data-mp="ddbk3dc891hc">{children}</span>
        {copyable && <CopyButton value={copyable} label={label} size={12} data-id="element-3105" data-mp="8f1pp4ybu4ws" />}
      </dd>
    </div>;
}
// ── Payout calculation ─────────────────────────────────────────────────────
// A dynamic, vertically-stacked breakdown. Only-applicable, non-zero signed
// line items render, grouped into a stable, bank/market-agnostic set of
// merchant-facing categories: Payment activity / Fees and taxes / Reserve
// adjustments / Minimum balance adjustments / Account charges / Other
// adjustments. Gross payment amount anchors the top and Net payout closes the
// bottom; everything between is bracketed as the adjustment set that sums to
// Net adjustments, so new adjustment kinds appear here automatically — no new
// columns and no reserved empty rows.
function PayoutCalculationSection({
  payout
}: {
  payout: Payout;
}) {
  const sections = getPayoutCalcSections(payout);
  const hasAdjustments = sections.length > 0;
  return <Section icon={ReceiptIcon} title="Payout calculation" data-id="element-3106" data-mp="1ilnxyacg3r9u">
      <div className="px-6 pt-4 pb-5" data-id="element-3107" data-mp="1ncw9bbxc5mpn">
        {/* The starting point. Sits OUTSIDE the bracketed region below so it
         reads as the figure being adjusted, not as one of the adjustments. */}
        <div className="flex items-baseline justify-between gap-4" data-id="element-3108" data-mp="3epu7geeznub3">
          <span className="text-sm font-medium text-gray-900" data-id="element-3109" data-mp="3enhlzra0v1ta">
            Gross payment amount
          </span>
          <span className="text-base font-medium tabular-nums text-gray-900" data-id="element-3110" data-mp="35ow500k77uum">
            {fmtAmount(payout.grossAmount)}
          </span>
        </div>

        {hasAdjustments && (
      /* Everything from here down to the Net adjustments total is one
       bracketed region. The left rule makes the SCOPE of "Net adjustments"
       visible, so the term doesn't have to carry that job in words. */
      <div className="mt-4 border-l-2 border-gray-200 pl-4" data-id="element-3111" data-mp="3ofnineb7y7et">
            <dl className="space-y-5" data-id="element-3112" data-mp="3ohhum34iip6r">
              {sections.map(section => <div key={section.key} data-id="element-3113" data-mp="2i0mnvbxxjv9t">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2" data-id="element-3114" data-mp="2d0i5fgdgoqgy">
                    {section.label}
                  </dt>
                  <div className="space-y-1.5" data-id="element-3115" data-mp="sk0s50ur9gm7">
                    {section.rows.map((row, i) => <div key={`${section.key}-${i}`} className="flex items-center justify-between gap-4 text-sm" data-id="element-3116" data-mp="xf6g057px5cx" data-mpi={i}>
                        <span className="text-gray-600" data-id="element-3117" data-mp="16avvxyqed7ix">{row.label}</span>
                        <span className={`tabular-nums ${row.amount < 0 ? 'text-gray-600' : 'text-emerald-700'}`} data-id="element-3118" data-mp="1b7mcsoa1075e">
                          {fmtSigned(row.amount)}
                        </span>
                      </div>)}
                  </div>
                </div>)}
            </dl>

            {/* Closes the region: the total of everything inside it. */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between gap-4 text-sm" data-id="element-3119" data-mp="3n0kxiufqmo65">
              <span className="font-medium text-gray-900" data-id="element-3120" data-mp="1i1944ztcwy5n">Net adjustments</span>
              <span className="font-medium tabular-nums text-gray-900" data-id="element-3121" data-mp="1i3lsbuppk4r5">
                {fmtSigned(payout.netAdjustments)}
              </span>
            </div>
          </div>)}

        {/* Outside the region again: gross + net adjustments. */}
        <div className="mt-4 pt-4 border-t border-gray-300 flex items-baseline justify-between gap-4" data-id="element-3122" data-mp="1w21wbdw6rh5l">
          <span className="text-base font-semibold text-gray-900" data-id="element-3123" data-mp="20z21lml7ofvn">
            Net payout
          </span>
          {/* fmtAmount already carries the currency code — don't append it. */}
          <span className="text-lg font-bold tabular-nums text-gray-900" data-id="element-3124" data-mp="29s5h00ff32sx">
            {fmtAmount(payout.netAmount)}
          </span>
        </div>
      </div>
    </Section>;
}
// ── Right-rail Details ───────────────────────────────────────────────────
function DetailsRail({
  payout
}: {
  payout: Payout;
}) {
  // Operational reference only — no financial figures here. The Net payout
  // and its full breakdown live in the Payout calculation card so there's a
  // single source of truth for the numbers.
  const isPending = payout.status === 'pending';
  return <aside className="space-y-6" data-id="element-3125" data-mp="346ha6zolk4n">
      <Section title="Details" data-id="element-3126" data-mp="357rk98vi2lb">
        <dl className="px-6 py-4 space-y-4" data-id="element-3127" data-mp="34erts8c0dco">
          <DetailItem label="Payout ID" mono copyable={payout.payoutId} data-id="element-3128" data-mp="3uazfr333b55h">
            {payout.payoutId}
          </DetailItem>
          <DetailItem label="Payout description" data-id="element-3129" data-mp="vrsl9gctmqs1">{BRAND.payoutNote}</DetailItem>
          {/* Two genuinely distinct dates, kept together so the relationship
           between them is readable: the record was created when the cycle cut
           the batch, and it is submitted on the payout date. Every payout has
           both, including pending ones. */}
          <DetailItem label="Created at" data-id="element-3130" data-mp="1ej1sfiz4d3gg">{payout.createdAt}</DetailItem>
          <DetailItem label={isPending ? 'Scheduled for' : 'Payout date'} data-id="element-3131" data-mp="1eleiykiy0m54">
            {payout.payoutDate}
          </DetailItem>
          {/* Status is the pill in the page header, currency is on the amount
           there, and the destination has its own card — none are repeated. */}
        </dl>
      </Section>
    </aside>;
}
function DetailItem({
  label,
  children,
  mono,
  copyable
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}) {
  return <div data-id="element-3132" data-mp="2z7dzehcx0o3d">
      <dt className="text-xs font-medium text-gray-500 mb-1" data-id="element-3133" data-mp="2l9z4wy7t1q4j">{label}</dt>
      <dd className="flex items-center gap-1.5 min-w-0" data-id="element-3134" data-mp="2l8o2b1q3iof0">
        <span className={`text-sm text-gray-900 min-w-0 truncate ${mono ? 'font-mono' : ''}`} data-id="element-3135" data-mp="2pbt1auayw7yw">
          {children}
        </span>
        {copyable && <CopyButton value={copyable} label={typeof label === 'string' ? label : 'value'} size={12} data-id="element-3136" data-mp="2gamiuforcn30" />}
      </dd>
    </div>;
}
// ── Transfers list ───────────────────────────────────────────────────────
function TransfersSection({
  payout
}: {
  payout: Payout;
}) {
  if (payout.transfers.length <= 1) return null;
  return <Section icon={ActivityIcon} title="Payout transfers" description={`This payout was sent as ${payout.transfers.length} separate transfers.`} data-id="element-3137" data-mp="1yz7ing1qkd0c">
      <div className="px-6 pt-3 pb-4 overflow-x-auto" data-id="element-3138" data-mp="28swrahhk37hu">
        <table className="w-full text-sm" data-id="element-3139" data-mp="2403m5si6dx58">
          <thead data-id="element-3140" data-mp="23z2c9rcwbgp3">
            <tr className="text-xs text-gray-500 border-b border-gray-100" data-id="element-3141" data-mp="23yk725899s51">
              <th className="text-left font-medium py-2 pr-4" data-id="element-3142" data-mp="2hqgsjqff53sg">Transfer ID</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3143" data-mp="2crx6zr4qlu0m">Status</th>
              <th className="text-left font-medium py-2 px-3" data-id="element-3144" data-mp="2rpxx24ixpbmu">
                <span className="inline-flex items-center" data-id="element-3145" data-mp="2ro51bk9v0btm">
                  <span data-id="element-3146" data-mp="2ms66pexxtxkr">Payout transfer reference</span>
                  <InfoTooltip content="A reference used to identify this payout transfer. It may appear on your bank statement." label="What is the payout transfer reference?" placement="top" align="left" data-id="element-3147" data-mp="xc6rph7n1dx7" />
                </span>
              </th>
              <th className="text-right font-medium py-2 pl-3" data-id="element-3148" data-mp="3kjl130fz8cju">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-3149" data-mp="3pl1wwhjlt8tk">
            {payout.transfers.map((t, idx) => <tr key={t.transferId} className="align-top" data-id="element-3150" data-mp="3tkslcelnrcux" data-mpi={idx}>
                <td className="py-3 pr-4" data-id="element-3151" data-mp="2wvl8duz3uc2m">
                  <div className="font-mono text-gray-900" data-id="element-3152" data-mp="2wwmj1yc7kwjt">{t.transferId}</div>
                  {/* Sequence position only. Transfers in a payout are all
                   submitted in the same batch, so none of them has a
                   timestamp of its own — the payout date in the Details panel
                   is the moment for every row here. */}
                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-3153" data-mp="2e5db3ti36fuc">
                    Transfer {idx + 1} of {payout.transfers.length}
                  </div>
                </td>
                <td className="py-3 px-3" data-id="element-3154" data-mp="2amo0t9dpruu2">
                  <TransferStatusBadge status={t.status} data-id="element-3155" data-mp="2alcw97cg2j1w" />
                </td>
                <td className="py-3 px-3" data-id="element-3156" data-mp="25nbdyomuenrh">
                  <div className="flex items-center gap-1.5 min-w-0" data-id="element-3157" data-mp="25ocqyynf5kbu">
                    <span className="font-mono text-gray-700 truncate" data-id="element-3158" data-mp="21kn2e96qv2qt">
                      {t.bankReference}
                    </span>
                    <CopyButton value={t.bankReference} label="payout transfer reference" size={12} data-id="element-3159" data-mp="1wp7q3lodxewi" />
                  </div>
                </td>
                <td className="py-3 pl-3 text-right tabular-nums font-medium text-gray-900 whitespace-nowrap" data-id="element-3160" data-mp="1hxgzk2mvkr54">
                  {fmtAmount(t.netAmount)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </Section>;
}
function TransferStatusBadge({
  status
}: {
  status: 'submitted' | 'failed' | 'cancelled' | 'pending';
}) {
  const styles: Record<typeof status, string> = {
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-gray-50 text-gray-600 border-gray-200',
    pending: 'bg-gray-50 text-gray-700 border-gray-200'
  };
  const label = status === 'submitted' ? 'Submitted' : status === 'failed' ? 'Failed' : status === 'cancelled' ? 'Cancelled' : 'Pending';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${styles[status]}`} data-id="element-3161" data-mp="1d36635qqi6qp">
      {label}
    </span>;
}
// ── Export: CSV helpers ────────────────────────────────────────────────────
const round2 = (n: number) => Math.round(n * 100) / 100;
function escCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadBlob(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
const csvDate = () => new Date().toISOString().slice(0, 10);
// Payout summary — payout-level reconciliation fields (one row).
function downloadPayoutSummaryCsv(payout: Payout, merchantName: string) {
  const headers = ['Payout ID', 'Merchant name', 'Status', 'Payout date', 'Currency', 'Gross Amount', 'Net adjustments', 'Net payout', 'Destination Account', 'Payout transfer references', 'Number of transfers'];
  const row = [escCsv(payout.payoutId), escCsv(merchantName), escCsv(payout.status), escCsv(payout.payoutDate), escCsv(payout.currency), payout.grossAmount.toFixed(2), payout.netAdjustments.toFixed(2), payout.netAmount.toFixed(2), escCsv(payout.destinationAccount),
  // The payout has no reference of its own; list each transfer's reference.
  escCsv(payout.transfers.map(t => t.bankReference).join('; ')), payout.transfers.length].join(',');
  downloadBlob(`${BRAND.name.toLowerCase()}-payout-${payout.payoutId}-summary-${csvDate()}.csv`, '\uFEFF' + [headers.join(','), row].join('\n'));
}
// Transaction details — transaction-level rows. MDR / fixed fee detail lives
// here (deliberately kept off the main page). Deterministic per payout.
interface PayoutTxnRow {
  transactionId: string;
  orderReference: string;
  grossAmount: number;
  mdrRate: number;
  percentageFee: number;
  fixedFee: number;
  totalProcessingFee: number;
  vatOnFee: number;
  whtAdjustment: number;
  reserveWithheld: number;
  netTransactionAmount: number;
  amountIncludedInPayout: number;
  payoutStatus: string;
}
function generatePayoutTransactions(payout: Payout): PayoutTxnRow[] {
  const seed = payout.payoutId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = (i: number) => {
    const x = Math.sin(seed * 7 + i * 13) * 10000;
    return x - Math.floor(x);
  };
  const MDR = 0.02826;
  const FIXED = 0.3;
  const rows: PayoutTxnRow[] = [];
  const count = 8 + Math.floor(rng(0) * 8);
  let remainingGross = payout.grossAmount;
  const base = payout.grossAmount / count;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const gross = isLast ? round2(remainingGross) : round2(Math.max(100, base * (0.5 + rng(i + 10))));
    const actualGross = Math.min(gross, remainingGross);
    remainingGross = round2(remainingGross - actualGross);
    const percentageFee = -round2(actualGross * MDR);
    const fixedFee = -FIXED;
    const totalProcessingFee = round2(percentageFee + fixedFee);
    const vatOnFee = -round2(Math.abs(totalProcessingFee) * 0.07);
    const whtAdjustment = round2(actualGross * 0.000847);
    const reserved = rng(i + 30) > 0.7;
    const reserveWithheld = reserved ? -round2(actualGross * 0.2) : 0;
    const netTransactionAmount = round2(actualGross + totalProcessingFee + vatOnFee + whtAdjustment);
    const amountIncludedInPayout = round2(netTransactionAmount + reserveWithheld);
    rows.push({
      transactionId: `txn_${String(10000 + Math.floor(rng(i + 20) * 90000))}`,
      orderReference: `ord_${String(100000 + Math.floor(rng(i + 40) * 900000))}`,
      grossAmount: actualGross,
      mdrRate: MDR,
      percentageFee,
      fixedFee,
      totalProcessingFee,
      vatOnFee,
      whtAdjustment,
      reserveWithheld,
      netTransactionAmount,
      amountIncludedInPayout,
      payoutStatus: reserved ? 'Partially reserved' : 'Included in payout'
    });
  }
  return rows;
}
function downloadTransactionDetailsCsv(payout: Payout) {
  const headers = ['Transaction ID', 'Order reference', 'Gross transaction amount', 'MDR rate', 'Percentage fee', 'Fixed fee', 'Total processing fee', 'VAT on fee', 'WHT adjustment', 'Reserve withheld', 'Net transaction amount', 'Amount included in payout', 'Transaction payout status'];
  const rows = generatePayoutTransactions(payout).map(t => [escCsv(t.transactionId), escCsv(t.orderReference), t.grossAmount.toFixed(2), `${(t.mdrRate * 100).toFixed(3)}%`, t.percentageFee.toFixed(2), t.fixedFee.toFixed(2), t.totalProcessingFee.toFixed(2), t.vatOnFee.toFixed(2), t.whtAdjustment.toFixed(2), t.reserveWithheld.toFixed(2), t.netTransactionAmount.toFixed(2), t.amountIncludedInPayout.toFixed(2), escCsv(t.payoutStatus)].join(','));
  downloadBlob(`${BRAND.name.toLowerCase()}-payout-${payout.payoutId}-transactions-${csvDate()}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ── Export menu ──────────────────────────────────────────────────────────
function ExportMenu({
  payout
}: {
  payout: Payout;
}) {
  const [open, setOpen] = useState(false);
  const {
    selectedCompany
  } = useCompany();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative inline-block" data-id="element-3162" data-mp="3ggxnkqwk6p44">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500" data-id="element-3163" data-mp="3ggfq5qy5u4um">
        <DownloadIcon size={16} aria-hidden="true" data-id="element-3164" data-mp="33m5x7ugc2pm" />
        Export
        <ChevronDownIcon size={14} aria-hidden="true" data-id="element-3165" data-mp="mnkyr0f7rbp4" />
      </button>
      {open && <div role="menu" className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-3166" data-mp="qpy59r1sbdmy">
          <button type="button" role="menuitem" onClick={() => {
        downloadPayoutSummaryCsv(payout, selectedCompany.name);
        setOpen(false);
      }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3167" data-mp="crrf6x32uhxu">
            <FileTextIcon size={14} className="text-gray-400" aria-hidden="true" data-id="element-3168" data-mp="3q534n2bfe8mw" />
            <div data-id="element-3169" data-mp="3v45zjtxoyyh5">
              <div className="font-medium" data-id="element-3170" data-mp="3v1jvw5dx9347">Download payout summary</div>
              <div className="text-xs text-gray-500" data-id="element-3171" data-mp="3g7opd618npw6">
                Payout-level reconciliation
              </div>
            </div>
          </button>
          <button type="button" role="menuitem" onClick={() => {
        downloadTransactionDetailsCsv(payout);
        setOpen(false);
      }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3172" data-mp="saqe08xpgiev">
            <FileTextIcon size={14} className="text-gray-400" aria-hidden="true" data-id="element-3173" data-mp="dmvh0gcnzrvz" />
            <div data-id="element-3174" data-mp="8obxemljms6s">
              <div className="font-medium" data-id="element-3175" data-mp="8ko3vng3m5s2">Download transaction details</div>
              <div className="text-xs text-gray-500" data-id="element-3176" data-mp="4k46qjviz25x">
                One row per transaction
              </div>
            </div>
          </button>
        </div>}
    </div>;
}
// ── Page ─────────────────────────────────────────────────────────────────
export function PayoutDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const payout = id ? getPayoutById(id) : undefined;
  if (!payout) {
    return <div className="max-w-4xl mx-auto py-16 text-center" data-id="element-3177" data-mp="1wljojgp48ohq">
        <p className="text-gray-600 mb-4" data-id="element-3178" data-mp="1wixffcmku0xe">Payout not found.</p>
        <Link to="/payouts" className="text-sm font-medium text-yellow-700 hover:text-yellow-800" data-id="element-3179" data-mp="21ia3g0p6pe1k">
          ← Back to Payouts
        </Link>
      </div>;
  }
  return <div className="max-w-[1280px] mx-auto pb-12" data-id="element-3180" data-mp="2b94stdxq8tr8">
      {/* Back link */}
      <button type="button" onClick={() => navigate('/payouts')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 rounded" data-id="element-3181" data-mp="1dmz2i32309ay">
        <ArrowLeftIcon size={14} aria-hidden="true" data-id="element-3182" data-mp="1sjpu3q5ikgq0" />
        Payouts
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4" data-id="element-3183" data-mp="2z01s9r8c5yll">
        <div className="min-w-0" data-id="element-3184" data-mp="33ybo841vx389">
          <div className="text-xs font-semibold uppercase tracking-wider text-yellow-700 mb-2" data-id="element-3185" data-mp="33xady1sp0krl">
            Payouts
          </div>
          <div className="flex items-baseline gap-3 flex-wrap mb-2" data-id="element-3186" data-mp="13u97l14c6ghb">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900" data-id="element-3187" data-mp="yvxy4rhye7zm">
              {fmtAmount(payout.netAmount)}
            </h1>
            <span className="text-xl text-gray-500 font-medium" data-id="element-3188" data-mp="1cuo3euexfdz2">
              {payout.currency}
            </span>
            <StatusPill status={payout.status} data-id="element-3189" data-mp="l5m2wjq2nt49" />
          </div>
          {/* No date line here — the payout's dates are stated once, as a
           labelled pair, in the Details panel. */}
        </div>
        <div className="flex-shrink-0" data-id="element-3190" data-mp="txwdw38sk2mk">
          <ExportMenu payout={payout} data-id="element-3191" data-mp="tyxyaybp61gy" />
        </div>
      </div>

      {/* Body: 2-col on desktop, stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-id="element-3192" data-mp="p5mp4qjex8lv">
        <div className="space-y-6 min-w-0" data-id="element-3193" data-mp="1gk0uzu9whie">
          <AccountDetails payout={payout} data-id="element-3194" data-mp="1firczswo32s" />

          <PayoutCalculationSection payout={payout} data-id="element-3195" data-mp="3tm7du629kwa0" />

          <TransfersSection payout={payout} data-id="element-3196" data-mp="3tjl8md2uisuy" />
        </div>

        <DetailsRail payout={payout} data-id="element-3197" data-mp="3jgpdkhlvpgbt" />
      </div>
    </div>;
}